import { lazy } from "react";
import { Navigate, Outlet, useParams, type RouteObject } from "react-router-dom";
import { ClientOnlyRoute } from "../../we0-runtime/client-only-route";
import {
  createSsrPage,
  type SsrPageMetadata,
} from "../../we0-runtime/ssr/create-ssr-page";
import RootLayout, { SiteLayout } from "../app/layout";
import HomePage from "../app/page";
import AboutPage from "../app/about/page";
import CasesPage from "../app/cases/page";
import ContactPage from "../app/contact/page";
import InsightsPage from "../app/insights/page";
import InsightArticlePage from "../app/insights/[slug]/page";
import PricingPage from "../app/pricing/page";
import PrivacyPage from "../app/privacy/page";
import ServicesPage from "../app/services/page";
import ServiceLandingPage from "../app/services/[slug]/page";
import SolutionsPage from "../app/solutions/page";
import SolutionLandingPage from "../app/solutions/[slug]/page";
import TermsPage from "../app/terms/page";
import WorkspacePage from "../app/workspace/page";
import { serviceLandingPages, solutionLandingPages } from "../lib/landing-pages";
import { siteName, siteUrl } from "../lib/seo";
import type { ArticleListing, ArticleRecord } from "../lib/we0-article-store";

const Back = lazy(() => import("../app/admin/back"));

type InsightsSsrData = {
  listings?: ArticleListing[];
  article?: ArticleRecord | null;
};

function apiOrigin(headers: Record<string, string | undefined>) {
  const protocol = headers["x-forwarded-proto"]?.split(",")[0] ?? "http";
  const host = headers.host ?? "localhost:3000";
  return `${protocol}://${host}`;
}

function recordValue(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function stringValue(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function stringArray(value: unknown) {
  return Array.isArray(value)
    ? value.filter(
        (item): item is string =>
          typeof item === "string" && Boolean(item.trim()),
      )
    : [];
}

function articleSsrMetadata(article: ArticleRecord): SsrPageMetadata {
  const seo = article.seo;
  const openGraph = recordValue(seo.openGraph);
  const twitter = recordValue(seo.twitter);
  const twitterImages = stringArray(twitter.images);
  const alternates = recordValue(recordValue(seo.alternates).languages);
  const structuredData = recordValue(seo.structuredData);
  const seoTitle = stringValue(seo.title) ?? article.title;
  const title = seoTitle.includes(siteName) ? seoTitle : `${seoTitle}｜${siteName}`;
  const description =
    stringValue(seo.description) ?? (article.summary || article.title);
  const canonical = new URL(
    `/insights/${encodeURIComponent(article.slug)}`,
    siteUrl,
  ).toString();
  const openGraphImages = Array.isArray(openGraph.images)
    ? openGraph.images
        .map(recordValue)
        .flatMap((image) => {
          const url = stringValue(image.url);
          const alt = stringValue(image.alt);
          return url ? [{ url, ...(alt ? { alt } : {}) }] : [];
        })
    : [];
  if (openGraphImages.length === 0 && article.coverImageUrl) {
    openGraphImages.push({
      url: article.coverImageUrl,
      alt: article.coverImageAlt ?? article.title,
    });
  }
  const jsonLd = Object.values(structuredData).filter(
    (value): value is Record<string, unknown> =>
      typeof value === "object" && value !== null && !Array.isArray(value),
  );
  if (jsonLd.length === 0) {
    const schema = recordValue(seo.schema);
    jsonLd.push(
      Object.keys(schema).length > 0
        ? { "@context": "https://schema.org", ...schema }
        : {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description,
            datePublished: article.publishedAt,
            dateModified: article.updatedAt,
            mainEntityOfPage: canonical,
            image: article.coverImageUrl ? [article.coverImageUrl] : undefined,
            publisher: {
              "@type": "Organization",
              name: siteName,
              url: siteUrl,
            },
          },
    );
  }

  return {
    title,
    description,
    keywords: stringArray(seo.keywords),
    canonical,
    robots: stringValue(seo.robots) ?? "index,follow",
    language: article.language,
    alternates: Object.fromEntries(
      Object.entries(alternates).flatMap(([language, href]) => {
        const value = stringValue(href);
        return value ? [[language, value]] : [];
      }),
    ),
    openGraph: {
      type: stringValue(openGraph.type) ?? "article",
      title: stringValue(openGraph.title) ?? title,
      description: stringValue(openGraph.description) ?? description,
      url: canonical,
      siteName,
      locale:
        stringValue(openGraph.locale) ?? article.language.replace("-", "_"),
      images: openGraphImages,
      publishedTime: article.publishedAt,
      modifiedTime: article.updatedAt,
    },
    twitter: {
      card: stringValue(twitter.card) ?? "summary_large_image",
      title: stringValue(twitter.title) ?? title,
      description: stringValue(twitter.description) ?? description,
      images:
        twitterImages.length > 0
          ? twitterImages
          : openGraphImages.map((image) => image.url),
    },
    jsonLd,
  };
}

export const InsightsRoute = createSsrPage<InsightsSsrData>({
  key: "insights",
  component: ({ ssrData }) => {
    const { slug } = useParams();

    if (slug) {
      return ssrData?.article ? (
        <InsightArticlePage article={ssrData.article} />
      ) : (
        <Navigate to="/insights" replace />
      );
    }

    return <InsightsPage publishedInsights={ssrData?.listings ?? []} />;
  },
  getSsrData: async ({ pathname, headers }) => {
    const slug = pathname.startsWith("/insights/")
      ? pathname.slice("/insights/".length)
      : "";
    const query = slug ? `?slug=${encodeURIComponent(slug)}` : "";

    try {
      const response = await fetch(
        `${apiOrigin(headers)}/api/we0/articles/upsert${query}`,
      );
      if (!response.ok) return slug ? { article: null } : { listings: [] };
      const result = (await response.json()) as {
        ok: boolean;
        data?: ArticleListing[] | ArticleRecord;
      };

      return slug
        ? { article: result.ok ? (result.data as ArticleRecord) : null }
        : { listings: result.ok ? (result.data as ArticleListing[]) : [] };
    } catch {
      return slug ? { article: null } : { listings: [] };
    }
  },
  getSsrMetadata: (_context, data) =>
    data.article
      ? articleSsrMetadata(data.article)
      : {
          title: `品牌增长与 SEO/GEO 洞察｜${siteName}`,
          description:
            "分享企业品牌增长、产品出海、内容营销、SEO 与 GEO 优化方法。",
          canonical: new URL("/insights", siteUrl).toString(),
          robots: "index,follow",
        },
});

export const routes: RouteObject[] = [
  {
    element: (
      <SiteLayout>
        <Outlet />
      </SiteLayout>
    ),
    children: [
      { path: "/", element: <HomePage /> },
      { path: "/about", element: <AboutPage /> },
      { path: "/cases", element: <CasesPage /> },
      { path: "/contact", element: <ContactPage /> },
      { path: "/insights", element: <InsightsRoute /> },
      { path: "/insights/:slug", element: <InsightsRoute /> },
      { path: "/pricing", element: <PricingPage /> },
      { path: "/privacy", element: <PrivacyPage /> },
      { path: "/services", element: <ServicesPage /> },
      ...serviceLandingPages.map((page) => ({
        path: page.path,
        element: <ServiceLandingPage slug={page.slug} />,
      })),
      { path: "/services/:slug", element: <Navigate to="/services" replace /> },
      { path: "/solutions", element: <SolutionsPage /> },
      ...solutionLandingPages.map((page) => ({
        path: page.path,
        element: <SolutionLandingPage slug={page.slug} />,
      })),
      { path: "/solutions/:slug", element: <Navigate to="/solutions" replace /> },
      { path: "/terms", element: <TermsPage /> },
    ],
  },
  { path: "/workspace", element: <WorkspacePage /> },
  {
    path: "/cms/*",
    element: (
      <ClientOnlyRoute>
        <Back />
      </ClientOnlyRoute>
    ),
  },
  { path: "*", element: <Navigate to="/" replace /> },
];
